// src/pages/Admin/Home/HomeSectionsManager.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getAllHomeSections,
  toggleHomeSectionActive,
  changeHomeSectionPosition,
  updateHomeSection,
} from "../../../services/homeSectionsServices";
import Button from "../../../components/Button/Button";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { FaSyncAlt, FaInfoCircle, FaEye, FaEyeSlash } from "react-icons/fa";

/* Helpers */
const isImageUrl = (url = "") =>
  /\.(png|jpe?g|webp|gif|avif|bmp|svg)(\?.*)?$/i.test(url) ||
  url.includes("res.cloudinary.com");

const ytId = (url = "") => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.hostname.includes("youtube.com")) return u.searchParams.get("v");
  } catch (_) {}
  return null;
};

const clamp = (n, min, max) => Math.max(min, Math.min(n, max));

export default function HomeSectionsManager() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [mediaInputs, setMediaInputs] = useState({}); // { [id]: value }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await getAllHomeSections(false); // admin: tout voir
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => a.position - b.position)
        : [];
      setSections(sorted);
      // init media inputs
      const seed = {};
      sorted.forEach((s) => (seed[s.id] = s.mediaURL || ""));
      setMediaInputs(seed);
    } catch (e) {
      toast.error("Impossible de charger les sections.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    setSavingId(id);
    try {
      // Optimistic UI
      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, is_active: !s.is_active } : s))
      );
      const res = await toggleHomeSectionActive(id);
      if (!res) throw new Error();
      toast.success("Visibilité mise à jour.");
    } catch (e) {
      // revert
      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, is_active: !s.is_active } : s))
      );
      toast.error("Échec de la mise à jour de la visibilité.");
    } finally {
      setSavingId(null);
    }
  };

  // DnD: réordonne seulement localement (aucun appel API)
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const srcIdx = source.index;
    const destIdx = destination.index;
    if (srcIdx === destIdx) return;

    // ⚡ Optimistic UI : on réordonne localement pour un ressenti instantané
    const local = [...sections];
    const [moved] = local.splice(srcIdx, 1);
    local.splice(destIdx, 0, moved);
    setSections(local);

    try {
      // 🛰️ Persistance serveur : on déplace l’élément à la position cible (1..N)
      const id = Number(String(draggableId).replace("sec_", ""));
      const targetPos = destIdx + 1; // position 1-based

      const updatedList = await changeHomeSectionPosition(id, targetPos);
      if (!updatedList) throw new Error("API returned empty");

      // 🔁 On resynchronise avec l’ordre réel renvoyé par le serveur
      const sorted = [...updatedList].sort((a, b) => a.position - b.position);
      setSections(sorted);
      toast.success("Ordre mis à jour.");
    } catch (e) {
      // ⛑️ En cas d’échec, on recharge depuis la BDD pour éviter tout décalage
      toast.error("Échec du réordonnancement. Rechargement…");
      refresh();
    }
  };

  // Edition précise par input position (enregistre sur le serveur)
  const handlePositionInput = async (id, val) => {
    const position = clamp(parseInt(val || "1", 10) || 1, 1, sections.length);
    try {
      const updated = await changeHomeSectionPosition(id, position);
      if (!updated) throw new Error();
      const sorted = [...updated].sort((a, b) => a.position - b.position);
      setSections(sorted);
      toast.success("Position mise à jour.");
    } catch (e) {
      toast.error("Position invalide ou erreur serveur.");
    }
  };

  // MediaURL input visible UNIQUEMENT pour ids 17, 18, 19
  const canEditMedia = (id) => [17, 18, 19].includes(Number(id));
  const handleMediaInputChange = (id, value) => {
    setMediaInputs((prev) => ({ ...prev, [id]: value }));
  };
  const handleMediaSave = async (s) => {
    const id = s.id;
    const value = (mediaInputs[id] || "").trim();

    if (![17, 18, 19].includes(Number(id))) return;

    // Validation
    if (id === 17 || id === 18) {
      if (!value || !isImageUrl(value)) {
        return toast.error(
          "Veuillez fournir un lien d’image valide (png, jpg, webp, etc.)."
        );
      }
    }
    if (id === 19) {
      if (!value || !ytId(value)) {
        return toast.error("Veuillez fournir un lien YouTube valide.");
      }
    }

    setSavingId(id);
    try {
      const res = await updateHomeSection(id, { mediaURL: value });
      if (!res) throw new Error();
      setSections((prev) =>
        prev.map((x) => (x.id === id ? { ...x, mediaURL: value } : x))
      );
      toast.success("mediaURL mis à jour.");
    } catch (e) {
      toast.error("Échec de la mise à jour du mediaURL.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Gestion des Sections de la page Accueil
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1 flex items-center gap-2">
                <FaInfoCircle />
                Glissez pour visualiser un autre ordre (non enregistré). Le
                bouton Rafraîchir recharge l’ordre réel.
              </p>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1 flex items-center gap-2">
                Les images en miniatures affichées à gauche sont là pour exemple et ne reflètent pas l'affichage réel sur le site.
              </p>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1 flex items-center gap-2">
                Vous avez la possibilité de changer les images sur Contenu Photo #01 et #02, ainsi que Contenu Video #01.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                text="Rafraîchir"
                icon={FaSyncAlt}
                onClick={refresh}
                className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
              />
            </div>
          </div>

          {/* LIST */}
          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="h-28 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="home-sections-droppable">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-4"
                    >
                      {sections.map((s, index) => {
                        const youTube = ytId(s.mediaURL);
                        const isImg = isImageUrl(s.mediaURL || "");
                        const inactive = !s.is_active;

                        return (
                          <Draggable
                            key={`sec_${s.id}`}
                            draggableId={`sec_${s.id}`}
                            index={index}
                          >
                            {(drag) => (
                              <article
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                                className={`rounded-2xl border bg-white/80 dark:bg-slate-900/50 backdrop-blur px-4 sm:px-5 py-4 ring-1 ring-slate-200/70 dark:ring-slate-700/70
                                  ${
                                    inactive ? "opacity-70 grayscale-[30%]" : ""
                                  }
                                `}
                              >
                                <div className="flex flex-col sm:flex-row gap-4">
                                  {/* Media */}
                                  <div className="w-full sm:w-60 rounded-xl overflow-hidden border border-slate-200/70 dark:border-slate-700/70 bg-slate-50 dark:bg-slate-800/40">
                                    {s.mediaURL ? (
                                      youTube ? (
                                        <div className="aspect-video">
                                          <iframe
                                            className="w-full h-full"
                                            src={`https://www.youtube.com/embed/${youTube}`}
                                            title={s.section_name}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen
                                          />
                                        </div>
                                      ) : isImg ? (
                                        <img
                                          src={s.mediaURL}
                                          alt={s.section_name}
                                          className="w-full h-40 object-cover"
                                          onError={(e) => {
                                            e.currentTarget.src =
                                              "/img/no-image.jpg";
                                          }}
                                        />
                                      ) : (
                                        <div className="h-40 flex items-center justify-center text-xs text-slate-500">
                                          <span className="px-2 text-center break-all">
                                            {s.mediaURL}
                                          </span>
                                        </div>
                                      )
                                    ) : (
                                      <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                                        Aucun média
                                      </div>
                                    )}
                                  </div>

                                  {/* Infos */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm font-semibold">
                                            {s.position}
                                          </span>
                                          <h3 className="text-lg sm:text-xl font-semibold text-slate-800 dark:text-slate-100 truncate">
                                            {s.section_name}
                                          </h3>
                                          {s.is_active ? (
                                            <span className="ml-1 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
                                              <FaEye /> actif
                                            </span>
                                          ) : (
                                            <span className="ml-1 inline-flex items-center gap-1 text-slate-500 text-xs font-semibold">
                                              <FaEyeSlash /> masqué
                                            </span>
                                          )}
                                        </div>
                                        {/* Description affichée seulement */}
                                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                          {s.description || "—"}
                                        </p>
                                      </div>

                                      {/* Toggle */}
                                      <div className="shrink-0">
                                        <label className="inline-flex items-center cursor-pointer select-none">
                                          <input
                                            type="checkbox"
                                            className="sr-only"
                                            checked={!!s.is_active}
                                            onChange={() => handleToggle(s.id)}
                                            disabled={savingId === s.id}
                                          />
                                          <span
                                            className={`w-12 h-7 flex items-center rounded-full p-1 transition
                                              ${
                                                s.is_active
                                                  ? "bg-emerald-500/90"
                                                  : "bg-slate-300 dark:bg-slate-700"
                                              }
                                            `}
                                          >
                                            <span
                                              className={`bg-white w-5 h-5 rounded-full shadow transform transition
                                                ${
                                                  s.is_active
                                                    ? "translate-x-5"
                                                    : ""
                                                }
                                              `}
                                            />
                                          </span>
                                        </label>
                                      </div>
                                    </div>

                                    {/* Ligne d'actions: position + (mediaURL si id 17/18/19) */}
                                    <div className="mt-3 flex flex-col gap-3">
                                      <div className="flex items-center gap-2">
                                        <label className="text-xs text-slate-500 dark:text-slate-400">
                                          Position
                                        </label>
                                        <input
                                          type="number"
                                          min={1}
                                          max={sections.length}
                                          defaultValue={s.position}
                                          className="w-24 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 px-3 py-2 text-sm"
                                          onBlur={(e) =>
                                            Number(e.target.value) !==
                                            s.position
                                              ? handlePositionInput(
                                                  s.id,
                                                  e.target.value
                                                )
                                              : null
                                          }
                                        />
                                      </div>

                                      {canEditMedia(s.id) && (
                                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                          <input
                                            type="url"
                                            value={mediaInputs[s.id] ?? ""}
                                            onChange={(e) =>
                                              handleMediaInputChange(
                                                s.id,
                                                e.target.value
                                              )
                                            }
                                            placeholder={
                                              s.id === 19
                                                ? "Lien YouTube (https://www.youtube.com/watch?v=...)"
                                                : "Lien image (https://… .jpg/.png)"
                                            }
                                            inputMode="url"
                                            className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 px-3 py-2 text-sm"
                                          />
                                          <Button
                                            text="Changer"
                                            onClick={() => handleMediaSave(s)}
                                            className="!rounded-xl"
                                            disabled={savingId === s.id}
                                          />
                                        </div>
                                      )}

                                      {/* Slug (lecture seule) */}
                                      <div className="text-xs text-slate-500 dark:text-slate-400">
                                        ID HTML&nbsp;: <code>#{s.slug}</code>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </article>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </div>

      {/* Styles locaux */}
      <style>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}
